import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';

import noWaypointsPng from '@/assets/illustrations/empty/no-waypoints.png';

import { useWaypointStore } from '@/stores/waypoint-store';
import { WaypointItem } from '@/components/property/WaypointItem';
import { OperationalGuide } from '@/components/common/OperationalGuide';
import { WAYPOINT_TYPE_LABELS, WAYPOINT_TYPE_DISPLAY_ORDER } from '@/components/map/map-utils';
import { WaypointType } from '@/types/waypoint';

interface WaypointListProps {
  propertyId: string;
  onAddWaypoint: () => void;
}

export function WaypointList({ propertyId, onAddWaypoint }: WaypointListProps) {
  const { waypoints, isLoading, loadWaypoints } = useWaypointStore();
  const [collapsedTypes, setCollapsedTypes] = useState<Set<WaypointType>>(new Set());

  useEffect(() => {
    void loadWaypoints(propertyId);
  }, [propertyId, loadWaypoints]);

  const propertyWaypoints = waypoints.filter((wp) => wp.propertyId === propertyId);

  // Group by type in display order, omit types with no waypoints
  const groups = WAYPOINT_TYPE_DISPLAY_ORDER
    .map((type) => ({
      type,
      items: propertyWaypoints.filter((wp) => wp.type === type),
    }))
    .filter((group) => group.items.length > 0);

  function toggleCollapse(type: WaypointType) {
    setCollapsedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2
          className="field-label"
          data-testid="waypoints-section-header"
        >
          WAYPOINTS{propertyWaypoints.length > 0 ? ` · ${propertyWaypoints.length}` : ''}
        </h2>
        {propertyWaypoints.length > 0 && (
          <button
            type="button"
            onClick={onAddWaypoint}
            className="flex items-center gap-1 text-[var(--color-accent)] hover:opacity-80 active:opacity-60 transition-opacity text-body-xs"
            data-testid="add-waypoint-btn"
            aria-label="Add waypoint"
          >
            <Plus size={14} aria-hidden="true" />
            Add
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-[var(--color-text-placeholder)] text-body-sm">Loading…</p>
      ) : groups.length === 0 ? (
        <OperationalGuide
          illustration={noWaypointsPng}
          heading="No Waypoints Yet"
          subtext="Pin your first location on the map"
          ctaLabel="Add Waypoint"
          onCtaClick={onAddWaypoint}
          compact
          data-guide-id="waypoint-list-empty"
        />
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map(({ type, items }) => {
            const isCollapsed = collapsedTypes.has(type);
            const label = WAYPOINT_TYPE_LABELS[type];

            return (
              <div key={type} className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => toggleCollapse(type)}
                  className="flex items-center gap-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors w-full text-left"
                  aria-expanded={!isCollapsed}
                >
                  {isCollapsed ? (
                    <ChevronRight size={14} className="flex-shrink-0" />
                  ) : (
                    <ChevronDown size={14} className="flex-shrink-0" />
                  )}
                  <span className="field-label">
                    {label}s ({items.length})
                  </span>
                </button>

                {!isCollapsed && (
                  <div className="flex flex-col gap-2 pl-1">
                    {items.map((waypoint) => (
                      <WaypointItem
                        key={waypoint.id}
                        waypoint={waypoint}
                        propertyId={propertyId}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
